import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import intelligentSearchService from '../services/intelligentSearchService';
import productService from '../services/productService';
import { useAuth } from './AuthContext';
import { useTranslation } from 'react-i18next';

function normalizeNumericFilter(value) {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

// Types d'actions
const SEARCH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_QUERY: 'SET_QUERY',
  SET_FILTERS: 'SET_FILTERS',
  SET_RESULTS: 'SET_RESULTS',
  SET_SELECTED_LISTING: 'SET_SELECTED_LISTING',
  SET_VIEW_MODE: 'SET_VIEW_MODE',
  SET_MAP_CENTER: 'SET_MAP_CENTER',
  SET_MAP_ZOOM: 'SET_MAP_ZOOM',
  SET_ERROR: 'SET_ERROR',
  SET_SUGGESTIONS: 'SET_SUGGESTIONS',
  SET_PAGINATION: 'SET_PAGINATION',
  CLEAR_RESULTS: 'CLEAR_RESULTS',
  TOGGLE_FILTER_PANEL: 'TOGGLE_FILTER_PANEL',
  SET_SORT_OPTIONS: 'SET_SORT_OPTIONS',
  ADD_TO_FAVORITES: 'ADD_TO_FAVORITES',
  REMOVE_FROM_FAVORITES: 'REMOVE_FROM_FAVORITES',
  // Recherche produits
  SET_SEARCH_TYPE: 'SET_SEARCH_TYPE',
  SET_PRODUCT_RESULTS: 'SET_PRODUCT_RESULTS'
};

// État initial
const initialState = {
  // Recherche
  query: '',
  filters: {
    categoryId: null,
    condition: null,
    country: '', // ✅ PAS DE PAYS PAR DÉFAUT - Laisser l'utilisateur choisir
    city: '',
    minPrice: null,
    maxPrice: null,
    radius: 10,
    isAvailable: true,
    // isVerified: false, // ❌ SUPPRIMÉ - Ne pas filtrer par défaut pour avoir tous les résultats
    rating: null,
    features: [],
    priceType: 'daily' // hourly, daily, weekly, monthly
  },
  
  // Résultats
  results: {
    listings: [],
    total: 0,
    totalPages: 0,
    currentPage: 1,
    hasMore: false
  },
  
  // Interface
  isLoading: false,
  error: null,
  suggestions: [],
  selectedListing: null,
  selectedListingSource: null,
  viewMode: 'split', // 'list', 'map', 'split'
  isFilterPanelOpen: false,
  
  // Carte
  mapCenter: [36.7538, 3.0588], // Alger par défaut
  mapZoom: 10,
  mapBounds: null,
  
  // Tri et pagination
  sortBy: 'relevance', // relevance, price, rating, date, distance
  sortOrder: 'desc',
  pagination: {
    page: 1,
    limit: 10, // Pagination par lots de 10
    offset: 0
  },
  
  // Favoris et historique
  favorites: [],
  searchHistory: [],
  
  // Performance
  lastSearchTime: null,
  searchDuration: null,

  // Type de recherche : 'listings' | 'products'
  searchType: 'listings',

  // Résultats produits
  productResults: {
    products: [],
    total: 0
  }
};

function isMarketplaceTwinListing(listing) {
  return listing?.offer_type === 'product' || listing?.booking_settings?.is_product_order === true;
}

function filterStandardListings(listings = []) {
  if (!Array.isArray(listings) || listings.length === 0) {
    return [];
  }

  return listings.filter((listing) => !isMarketplaceTwinListing(listing));
}

// Reducer
function searchReducer(state, action) {
  switch (action.type) {
    case SEARCH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
        error: action.payload ? null : state.error
      };
      
    case SEARCH_ACTIONS.SET_QUERY:
      return {
        ...state,
        query: action.payload,
        pagination: { ...state.pagination, page: 1, offset: 0 }
      };
      
    case SEARCH_ACTIONS.SET_FILTERS:
      const reducerId = `reducer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      console.log(`\n🔄 ========== [SEARCH REDUCER:${reducerId}] SET_FILTERS ==========`);
      console.log(`📋 [SearchReducer:${reducerId}] TRAITEMENT ACTION SET_FILTERS:`, {
        action: {
          type: action.type,
          payload: action.payload,
          payloadKeys: action.payload ? Object.keys(action.payload) : []
        },
        currentState: {
          filters: state.filters,
          filtersKeys: state.filters ? Object.keys(state.filters) : []
        },
        mergeOperation: {
          willMerge: true,
          strategy: 'spread operator merge',
          newFilters: { ...state.filters, ...action.payload }
        },
        pagination: {
          willReset: true,
          newPagination: { ...state.pagination, page: 1, offset: 0 }
        },
        timestamp: new Date().toISOString()
      });
      
      const newFilters = { ...state.filters, ...action.payload };
      const newState = {
        ...state,
        filters: newFilters,
        pagination: { ...state.pagination, page: 1, offset: 0 }
      };
      
      console.log(`📝 [SearchReducer:${reducerId}] NOUVEL ÉTAT CRÉÉ:`, {
        newState: {
          filters: newState.filters,
          pagination: newState.pagination
        },
        changes: {
          filtersChanged: JSON.stringify(state.filters) !== JSON.stringify(newFilters),
          paginationReset: true
        }
      });
      
      console.log(`🏁 ========== [SEARCH REDUCER:${reducerId}] FIN SET_FILTERS ==========\n`);
      
      return newState;
      
    case SEARCH_ACTIONS.SET_RESULTS:
      const { listings, total, append = false, hasMore } = action.payload;
      const safeListings = filterStandardListings(listings);      
      
      // � OPTIMIZED FIX: Déduplication intelligente pour des milliers d'annonces
      let finalListings;
      
      if (append) {
        // 📊 ANALYSE RAPIDE: Détecter si déduplication nécessaire
        const suspectedDuplicatesInExisting = state.results.listings.length > total;
        const existingListings = state.results.listings;
        
        // 🔄 OPTIMISATION: Nettoyer les existants seulement si soupçon de doublons
        let cleanExistingListings;
        if (suspectedDuplicatesInExisting) {
          console.log('[SearchContext] 🔍 Suspected duplicates in existing listings, cleaning...');
          cleanExistingListings = Array.from(
            new Map(existingListings.map(listing => [listing.id, listing])).values()
          );
        } else {
          // ⚡ FAST PATH: Pas de nettoyage si pas de soupçons
          cleanExistingListings = existingListings;
        }
        
        // 🎯 PRÉVENTION: Filtrer seulement les nouveaux doublons (O(n))
        const existingIds = new Set(cleanExistingListings.map(listing => listing.id));
        const newListingsWithoutDuplicates = safeListings.filter(listing => !existingIds.has(listing.id));
               
        
        finalListings = [...cleanExistingListings, ...newListingsWithoutDuplicates];
        
      } else {
        // 🆕 NOUVEAU: Déduplication conditionnelle même pour le remplacement
        // Vérification rapide si doublons potentiels (évite le traitement inutile)
        const hasPotentialDuplicates = safeListings.length > 0 && 
          safeListings.length !== new Set(safeListings.map(l => l.id)).size;
        
        if (hasPotentialDuplicates) {
          console.log('[SearchContext] ⚠️ Potential duplicates detected in new listings, deduplicating...');
          finalListings = Array.from(
            new Map(safeListings.map(listing => [listing.id, listing])).values()
          );
                    
        } else {
          // ⚡ ULTRA FAST PATH: Aucune déduplication nécessaire
          finalListings = safeListings;
        }
      }
      
      // ─── TRI CÔTÉ CLIENT ─────────────────────────────────────────────────
      // Appliqué ici dans le reducer pour être garanti avant tout rendu,
      // quelle que soit la source des données (cache, backend, append).
      const sortGetPrice = (l) => {
        const candidates = [
          parseFloat(l.price_hourly),
          parseFloat(l.price_daily),
          parseFloat(l.price_weekly),
          parseFloat(l.price_monthly),
          parseFloat(l.booking_settings?.base_price),
          parseFloat(l.booking_settings?.price_per_slot),
        ];
        return candidates.find(v => v > 0) ?? null;
      };

      if (state.sortBy === 'price') {
        const sortDesc = state.sortOrder !== 'asc';
        finalListings = [...finalListings].sort((a, b) => {
          const pA = sortGetPrice(a);
          const pB = sortGetPrice(b);
          if (pA === null && pB === null) return 0;
          if (pA === null) return sortDesc ? -1 : 1;
          if (pB === null) return sortDesc ? 1 : -1;
          return sortDesc ? pB - pA : pA - pB;
        });
      } else if (state.sortBy === 'rating') {
        const sortDesc = state.sortOrder !== 'asc';
        finalListings = [...finalListings].sort((a, b) => {
          const rA = parseFloat(a.rating_avg) || 0;
          const rB = parseFloat(b.rating_avg) || 0;
          return sortDesc ? rB - rA : rA - rB;
        });
      } else if (state.sortBy === 'date') {
        const sortDesc = state.sortOrder !== 'asc';
        finalListings = [...finalListings].sort((a, b) => {
          const dA = new Date(a.createdAt || a.created_at).getTime() || 0;
          const dB = new Date(b.createdAt || b.created_at).getTime() || 0;
          return sortDesc ? dB - dA : dA - dB;
        });
      }
      // ─────────────────────────────────────────────────────────────────────

      return {
        ...state,
        results: {
          listings: finalListings,
          total,
          totalPages: Math.ceil(total / state.pagination.limit),
          currentPage: state.pagination.page,
          // Utiliser hasMore du backend si disponible, sinon calculer
          hasMore: hasMore !== undefined ? hasMore : (state.pagination.offset + safeListings.length) < total
        },
        isLoading: false,
        error: null,
        lastSearchTime: Date.now()
      };
      
    case SEARCH_ACTIONS.SET_SELECTED_LISTING:
      return {
        ...state,
        selectedListing: action.payload?.listing ?? action.payload,
        selectedListingSource: action.payload?.source || 'list'
      };
      
    case SEARCH_ACTIONS.SET_VIEW_MODE:
      return {
        ...state,
        viewMode: action.payload
      };
      
    case SEARCH_ACTIONS.SET_MAP_CENTER:
      return {
        ...state,
        mapCenter: action.payload
      };
      
    case SEARCH_ACTIONS.SET_MAP_ZOOM:
      return {
        ...state,
        mapZoom: action.payload
      };
      
    case SEARCH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
      
    case SEARCH_ACTIONS.SET_SUGGESTIONS:
      return {
        ...state,
        suggestions: action.payload
      };
      
    case SEARCH_ACTIONS.SET_PAGINATION:
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload }
      };
      
    case SEARCH_ACTIONS.CLEAR_RESULTS:
      return {
        ...state,
        results: initialState.results,
        selectedListing: null,
        selectedListingSource: null,
        error: null
      };
      
    case SEARCH_ACTIONS.TOGGLE_FILTER_PANEL:
      return {
        ...state,
        isFilterPanelOpen: !state.isFilterPanelOpen
      };
      
    case SEARCH_ACTIONS.SET_SORT_OPTIONS:
      return {
        ...state,
        sortBy: action.payload.sortBy || state.sortBy,
        sortOrder: action.payload.sortOrder || state.sortOrder,
        pagination: { ...state.pagination, page: 1, offset: 0 }
      };
      
    case SEARCH_ACTIONS.ADD_TO_FAVORITES:
      return {
        ...state,
        favorites: [...state.favorites, action.payload]
      };
      
    case SEARCH_ACTIONS.REMOVE_FROM_FAVORITES:
      return {
        ...state,
        favorites: state.favorites.filter(id => id !== action.payload)
      };

    case SEARCH_ACTIONS.SET_SEARCH_TYPE:
      return {
        ...state,
        searchType: action.payload
      };

    case SEARCH_ACTIONS.SET_PRODUCT_RESULTS:
      return {
        ...state,
        productResults: {
          products: action.payload.products || [],
          total: action.payload.total || 0
        }
      };
      
    default:
      return state;
  }
}

// Context
const SearchContext = createContext();

// Provider
export const SearchProvider = ({ children }) => {
  const [state, dispatch] = useReducer(searchReducer, initialState);
  const { user } = useAuth();
  const { t } = useTranslation();

  const fetchProductResults = useCallback(async ({
    query,
    filters,
    append = false,
  }) => {
    const selectedCategoryIds = Array.isArray(filters.categoryIds)
      ? filters.categoryIds.filter(Boolean)
      : [];
    const fallbackCategoryId = filters.categoryId || null;
    const uniqueCategoryIds = Array.from(new Set([
      ...selectedCategoryIds,
      ...(fallbackCategoryId ? [fallbackCategoryId] : []),
    ]));
    const minPrice = normalizeNumericFilter(filters.minPrice);
    const maxPrice = normalizeNumericFilter(filters.maxPrice);

    const productParams = {
      search: query || undefined,
      category_id: uniqueCategoryIds.length === 1 ? uniqueCategoryIds[0] : undefined,
      category_ids: uniqueCategoryIds.length > 1 ? uniqueCategoryIds.join(',') : undefined,
      condition: filters.condition || undefined,
      country_code: filters.country || undefined,
      cities: Array.isArray(filters.cities) && filters.cities.length > 0
        ? filters.cities.join(',')
        : undefined,
      min_price_cents: minPrice !== undefined ? Math.round(minPrice * 100) : undefined,
      max_price_cents: maxPrice !== undefined ? Math.round(maxPrice * 100) : undefined,
      lat: filters.lat || undefined,
      lng: filters.lng || undefined,
      radius_km: filters.radius || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      status: 'active',
      limit: 20,
      page: append ? undefined : 1,
    };

    Object.keys(filters).forEach((key) => {
      if (key.startsWith('details_')) {
        const value = filters[key];
        if (value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
          productParams[key] = value;
        }
      }
    });

    Object.keys(productParams).forEach((key) => productParams[key] === undefined && delete productParams[key]);

    const productResponse = await productService.search(productParams);
    const products = productResponse.products || [];

    dispatch({
      type: SEARCH_ACTIONS.SET_PRODUCT_RESULTS,
      payload: {
        products,
        total: productResponse.totalCount || products.length,
      },
    });

    return products;
  }, []);

  // Actions

  /**
   * Effectuer une recherche
   */
  const performSearch = useCallback(async (options = {}) => {
    const { 
      query = state.query, 
      filters = state.filters, 
      append = false,
      forceRefresh = false,
      preCalculatedOffset = null,  // 🎯 Nouveau paramètre pour éviter les race conditions
      // Permet d'exécuter une recherche cohérente même si on vient de changer le searchType
      // (le dispatch est async, donc state.searchType peut être encore l'ancien à cet instant)
      searchType: searchTypeOverride = undefined,
      // Overrides pour le tri (évite le stale-closure après dispatch SET_SORT_OPTIONS)
      sortBy: sortByOverride = undefined,
      sortOrder: sortOrderOverride = undefined
    } = options;

    // Lire searchType depuis l'override si fourni, sinon depuis l'état courant
    const currentSearchType = searchTypeOverride ?? state.searchType;

    const searchId = `context_search_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    console.log(`\n🚀 ========== [SEARCH CONTEXT:${searchId}] DÉBUT RECHERCHE ==========`);
    console.log(`📋 [SearchContext:${searchId}] PARAMÈTRES DE RECHERCHE:`, {
      inputParameters: {
        query: {
          value: query,
          type: typeof query,
          length: query ? query.length : 0,
          fromState: state.query,
          isOverride: query !== state.query
        },
        filters: {
          received: filters,
          fromState: state.filters,
          isOverride: JSON.stringify(filters) !== JSON.stringify(state.filters),
          criticalFilters: {
            country: filters.country,
            city: filters.city,
            cities: filters.cities,
            categoryId: filters.categoryId
          }
        },
        options: {
          append,
          forceRefresh,
          currentSearchType,
          searchTypeOverride: searchTypeOverride ?? null,
          hasOptions: Object.keys(options).length > 0
        }
      },
      currentState: {
        query: state.query,
        filtersCount: Object.keys(state.filters).length,
        currentPage: state.pagination.page,
        isLoading: state.isLoading,
        lastSearchTime: state.lastSearchTime,
        searchType: state.searchType
      },
      user: user ? { 
        id: user.id, 
        hasPreferences: !!user.preferences,
        preferencesKeys: user.preferences ? Object.keys(user.preferences) : []
      } : null,
      timestamp: new Date().toISOString()
    });

    try {
      console.log(`⏳ [SearchContext:${searchId}] DÉMARRAGE OPÉRATION DE RECHERCHE...`);
      dispatch({ type: SEARCH_ACTIONS.SET_LOADING, payload: true });
      
      const startTime = Date.now();
      
      // Préparer les paramètres de recherche avec protection race conditions
      const searchOptions = {
        offset: preCalculatedOffset !== null ? preCalculatedOffset : 
                append ? state.pagination.offset + state.pagination.limit : 0,
        sortBy: sortByOverride !== undefined ? sortByOverride : state.sortBy,
        sortOrder: sortOrderOverride !== undefined ? sortOrderOverride : state.sortOrder,
        forceRefresh
      };
      
      console.log(`🎯 [SearchContext:${searchId}] OFFSET CALCULATION:`, {
        preCalculatedOffset,
        append,
        currentOffset: state.pagination.offset,
        limit: state.pagination.limit,
        calculatedOffset: append ? state.pagination.offset + state.pagination.limit : 0,
        finalOffset: searchOptions.offset,
        source: preCalculatedOffset !== null ? 'pre-calculated (race-safe)' : 'calculated-on-demand'
      });
      
      console.log(`📤 [SearchContext → IntelligentSearchService:${searchId}] APPEL SERVICE:`, {
        serviceMethod: 'searchWithPreferences',
        parameters: {
          query: {
            value: query,
            type: typeof query
          },
          userPreferences: {
            provided: user?.preferences || {},
            hasData: !!(user?.preferences && Object.keys(user.preferences).length > 0),
            structure: user?.preferences ? Object.keys(user.preferences) : []
          },
          filters: {
            provided: filters,
            structure: Object.keys(filters),
            keyCount: Object.keys(filters).length,
            location: {
              country: filters.country,
              city: filters.city,
              cities: filters.cities
            },
            business: {
              categoryId: filters.categoryId,
              isAvailable: filters.isAvailable,
              isVerified: filters.isVerified
            }
          },
          searchOptions: {
            provided: searchOptions,
            structure: Object.keys(searchOptions)
          }
        },
        callContext: {
          hasUser: !!user,
          isAppend: append,
          forceRefresh: forceRefresh
        }
      });
      
      // Effectuer la recherche avec préférences utilisateur
      const results = await intelligentSearchService.searchWithPreferences(
        query,
        user?.preferences || {},
        filters,
        searchOptions
      );
      
      const searchDuration = Date.now() - startTime;
      
      console.log(`📥 [SearchContext ← IntelligentSearchService:${searchId}] RÉSULTATS REÇUS:`, {
        results: {
          hasResults: !!results,
          structure: results ? Object.keys(results) : [],
          listings: {
            hasListings: !!results.listings,
            count: results.listings?.length || 0,
            isArray: Array.isArray(results.listings)
          },
          metadata: {
            total: results.total || 0,
            hasMore: results.hasMore,
            hasMetadata: !!results.searchMetadata
          }
        },
        performance: {
          searchDuration: `${searchDuration}ms`,
          isAppend: append
        },
        context: {
          wasFromCache: results.searchMetadata?.cacheHit,
          searchId: results.searchMetadata?.searchId
        }
      });
      
    // Mettre à jour les résultats
    dispatch({
      type: SEARCH_ACTIONS.SET_RESULTS,
      payload: {
        listings: results.listings || [],
        total: results.total || 0,
        hasMore: results.hasMore, // Utiliser hasMore du backend
        append
      }
    });
    
    console.log(`✅ [SearchContext:${searchId}] ÉTAT MIS À JOUR:`, {
      stateUpdate: {
        newListingsCount: results.listings?.length || 0,
        totalInState: results.total || 0,
        hasMore: results.hasMore,
        append: append,
        action: 'SET_RESULTS dispatched'
      },
      previousState: {
        listingsCount: state.results.listings.length,
        total: state.results.total
      }
    });
    
    // Mettre à jour la pagination seulement si pas déjà fait
    if (append && preCalculatedOffset === null) {
      dispatch({
        type: SEARCH_ACTIONS.SET_PAGINATION,
        payload: {
          page: state.pagination.page + 1,
          offset: state.pagination.offset + state.pagination.limit
        }
      });
    } else if (append && preCalculatedOffset !== null) {
      console.log('[SearchContext] ✅ Pagination already updated proactively, skipping duplicate update');
    }

    // ─── Recherche produits (parallèle, si searchType inclut les produits) ───
    if (currentSearchType !== 'listings') {
      try {
        const products = await fetchProductResults({ query, filters, append });

        console.log(`🛍️ [SearchContext] Produits trouvés: ${products.length}`);
      } catch (productErr) {
        console.warn('[SearchContext] Product search error (non-blocking):', productErr.message);
        // Non bloquant : on réinitialise seulement si ce n'est pas un append
        if (!append) {
          dispatch({
            type: SEARCH_ACTIONS.SET_PRODUCT_RESULTS,
            payload: { products: [], total: 0 }
          });
        }
      }
    }
    
    console.log(`✅ [SearchContext] Search completed successfully in ${searchDuration}ms`);
    
    } catch (error) {
      console.error('❌ [SearchContext] Search error:', {
        errorMessage: error.message,
        errorStack: error.stack,
        searchParams: { query, filters, append, forceRefresh },
        timestamp: new Date().toISOString()
      });
      
      dispatch({
        type: SEARCH_ACTIONS.SET_ERROR,
        payload: {
          message: error.message || t('search.errors.general'),
          type: 'search_error'
        }
      });
    } finally {
      dispatch({ type: SEARCH_ACTIONS.SET_LOADING, payload: false });
    }
  }, [state.query, state.filters, state.pagination, state.sortBy, state.sortOrder, state.searchType, user?.preferences, t]);

  /**
   * Recherche forcée (ne dépend pas de l'état de la query)
   */
  const forceSearch = useCallback(async (customQuery = null, customFilters = null, append = false, customSearchType = null) => {
    try {
      dispatch({ type: SEARCH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: SEARCH_ACTIONS.SET_ERROR, payload: null });

      const searchStartTime = Date.now();
      
      const searchQuery = customQuery !== null ? customQuery : state.query;
      const searchFilters = customFilters !== null ? customFilters : state.filters;
      const currentSearchType = customSearchType ?? state.searchType;
      
      console.log('[SearchContext] Force search:', { 
        query: searchQuery, 
        filters: searchFilters,
        searchType: currentSearchType,
        append 
      });

      const results = await intelligentSearchService.searchWithPreferences(
        searchQuery,
        user?.preferences || {},
        {
          ...searchFilters,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
          limit: state.pagination.limit,
          offset: append ? state.pagination.offset : 0
        }
      );

      const searchDuration = Date.now() - searchStartTime;

  // Mettre à jour les résultats
  dispatch({
    type: SEARCH_ACTIONS.SET_RESULTS,
    payload: {
      listings: results.listings || [],
      total: results.total || 0,
      hasMore: results.hasMore, // Utiliser hasMore du backend
      append
    }
  });
  
  // Mettre à jour la pagination
  if (append) {
    dispatch({
      type: SEARCH_ACTIONS.SET_PAGINATION,
      payload: {
        page: state.pagination.page + 1,
        offset: state.pagination.offset + state.pagination.limit
      }
    });
  }

  if (currentSearchType !== 'listings') {
    try {
      const products = await fetchProductResults({
        query: searchQuery,
        filters: searchFilters,
        append,
      });

      console.log(`[SearchContext] Force search products completed: ${products.length} products`);
    } catch (productErr) {
      console.warn('[SearchContext] Force search product error (non-blocking):', productErr.message);
      if (!append) {
        dispatch({
          type: SEARCH_ACTIONS.SET_PRODUCT_RESULTS,
          payload: { products: [], total: 0 }
        });
      }
    }
  }
  
  console.log(`[SearchContext] Force search completed in ${searchDuration}ms`);    } catch (error) {
      console.error('[SearchContext] Force search error:', error);
      dispatch({
        type: SEARCH_ACTIONS.SET_ERROR,
        payload: {
          message: error.message || t('search.errors.general'),
          type: 'search_error'
        }
      });
    } finally {
      dispatch({ type: SEARCH_ACTIONS.SET_LOADING, payload: false });
    }
  }, [fetchProductResults, state.query, state.filters, state.pagination, state.sortBy, state.sortOrder, state.searchType, user?.preferences, t]);

  /**
   * Recherche géographique
   */
  const searchNearby = useCallback(async (coordinates, radius = state.filters.radius) => {
    try {
      dispatch({ type: SEARCH_ACTIONS.SET_LOADING, payload: true });
      
      const results = await intelligentSearchService.searchNearby(
        coordinates,
        radius,
        state.filters,
        user?.preferences || {}
      );
      
      dispatch({
        type: SEARCH_ACTIONS.SET_RESULTS,
        payload: {
          listings: results.listings || [],
          total: results.total || 0,
          append: false
        }
      });
      
      // Centrer la carte sur les coordonnées
      dispatch({ type: SEARCH_ACTIONS.SET_MAP_CENTER, payload: [coordinates.latitude, coordinates.longitude] });
      
    } catch (error) {
      console.error('[SearchContext] Nearby search error:', error);
      dispatch({
        type: SEARCH_ACTIONS.SET_ERROR,
        payload: {
          message: error.message || t('search.errors.nearby'),
          type: 'nearby_error'
        }
      });
    }
  }, [state.filters, user?.preferences, t]);

  /**
   * Obtenir des suggestions
   */
  const getSuggestions = useCallback(async (partialQuery) => {
    try {
      const suggestions = await intelligentSearchService.getSuggestions(
        partialQuery,
        {
          language: user?.preferences?.language || 'fr',
          preferredCategories: user?.preferences?.search?.preferredCategories || []
        }
      );
      
      dispatch({ type: SEARCH_ACTIONS.SET_SUGGESTIONS, payload: suggestions });
      
    } catch (error) {
      console.error('[SearchContext] Suggestions error:', error);
      dispatch({ type: SEARCH_ACTIONS.SET_SUGGESTIONS, payload: [] });
    }
  }, [user?.preferences]);

  /**
   * Mettre à jour la requête
   */
  const updateQuery = useCallback((query) => {
    dispatch({ type: SEARCH_ACTIONS.SET_QUERY, payload: query });
  }, []);

  /**
   * Changer le type de recherche : 'listings' | 'products' | 'both'
   */
  const updateSearchType = useCallback((type) => {
    dispatch({ type: SEARCH_ACTIONS.SET_SEARCH_TYPE, payload: type });
    // Fermer le floating card quand on change de type de recherche
    dispatch({ type: SEARCH_ACTIONS.SET_SELECTED_LISTING, payload: { listing: null, source: 'list' } });
  }, []);

  /**
   * Mettre à jour les filtres
   */
  const updateFilters = useCallback((newFilters) => {
    const updateId = `filter_update_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    console.log(`\n📝 ========== [SEARCH CONTEXT:${updateId}] MISE À JOUR FILTRES ==========`);
    console.log(`🔄 [SearchContext:${updateId}] PARAMÈTRES DE MISE À JOUR:`, {
      receivedFilters: {
        filters: newFilters,
        keys: newFilters ? Object.keys(newFilters) : [],
        count: newFilters ? Object.keys(newFilters).length : 0,
        criticalFilters: {
          country: newFilters?.country,
          city: newFilters?.city,
          cities: newFilters?.cities,
          categoryId: newFilters?.categoryId
        }
      },
      currentState: {
        filters: state.filters,
        keys: state.filters ? Object.keys(state.filters) : [],
        count: state.filters ? Object.keys(state.filters).length : 0
      },
      comparison: {
        hasChanged: JSON.stringify(state.filters) !== JSON.stringify(newFilters),
        newKeys: newFilters ? Object.keys(newFilters).filter(key => !(key in state.filters)) : [],
        modifiedKeys: newFilters ? Object.keys(newFilters).filter(key => 
          key in state.filters && state.filters[key] !== newFilters[key]
        ) : []
      },
      timestamp: new Date().toISOString()
    });
    
    console.log(`📋 [SearchContext:${updateId}] DISPATCH SET_FILTERS:`, {
      action: 'SET_FILTERS',
      payload: newFilters,
      willTriggerReducer: true
    });
    
    dispatch({ type: SEARCH_ACTIONS.SET_FILTERS, payload: newFilters });
    
    console.log(`🏁 ========== [SEARCH CONTEXT:${updateId}] FIN MISE À JOUR FILTRES ==========\n`);
  }, [state.filters]);

  /**
   * Sélectionner un listing
   */
  const selectListing = useCallback((listing, source = 'list') => {
    dispatch({ type: SEARCH_ACTIONS.SET_SELECTED_LISTING, payload: { listing, source } });
    
    // Centrer la carte sur le listing sélectionné
    if (listing && listing.latitude && listing.longitude) {
      dispatch({
        type: SEARCH_ACTIONS.SET_MAP_CENTER,
        payload: [listing.latitude, listing.longitude]
      });
    }
  }, []);

  /**
   * Changer le mode d'affichage
   */
  const changeViewMode = useCallback((mode) => {
    dispatch({ type: SEARCH_ACTIONS.SET_VIEW_MODE, payload: mode });
  }, []);

  /**
   * Mettre à jour la carte
   */
  const updateMapView = useCallback((center, zoom) => {
    if (center) {
      dispatch({ type: SEARCH_ACTIONS.SET_MAP_CENTER, payload: center });
    }
    if (zoom) {
      dispatch({ type: SEARCH_ACTIONS.SET_MAP_ZOOM, payload: zoom });
    }
  }, []);

  /**
   * Charger plus de résultats avec protection contre les race conditions
   */
  const loadMore = useCallback(() => {
    console.log('[SearchContext] loadMore called:', {
      hasMore: state.results.hasMore,
      isLoading: state.isLoading,
      currentListingsCount: state.results.listings.length,
      total: state.results.total,
      currentPage: state.pagination.page,
      currentOffset: state.pagination.offset
    });
    
    // 🛡️ FIX: Protection contre les appels multiples simultanés
    if (state.results.hasMore && !state.isLoading) {
      // 🔧 FIX: Mettre à jour la pagination AVANT l'appel pour éviter les race conditions
      const nextOffset = state.pagination.offset + state.pagination.limit;
      
      console.log('[SearchContext] 🚀 Updating pagination proactively to prevent race conditions:', {
        currentOffset: state.pagination.offset,
        nextOffset: nextOffset,
        limit: state.pagination.limit
      });
      
      // Mise à jour immédiate de la pagination pour bloquer les appels simultanés
      dispatch({
        type: SEARCH_ACTIONS.SET_PAGINATION,
        payload: {
          page: state.pagination.page + 1,
          offset: nextOffset
        }
      });
      
      // Appel avec l'offset déjà calculé (pas besoin de le recalculer dans performSearch)
      performSearch({ 
        append: true,
        preCalculatedOffset: nextOffset  // 🎯 Passer l'offset pré-calculé
      });
    } else {
      console.log('[SearchContext] loadMore blocked:', {
        reason: !state.results.hasMore ? 'no more results' : 'already loading'
      });
    }
  }, [state.results.hasMore, state.isLoading, state.results.listings.length, state.results.total, state.pagination, performSearch]);

  /**
   * Trier les résultats
   */
  const sortResults = useCallback((sortBy, sortOrder = 'desc') => {
    dispatch({
      type: SEARCH_ACTIONS.SET_SORT_OPTIONS,
      payload: { sortBy, sortOrder }
    });
    
    // Relancer la recherche avec le nouveau tri — passer les valeurs directement
    // pour éviter le stale-closure (state.sortBy/sortOrder n'est pas encore mis à jour)
    performSearch({ forceRefresh: true, sortBy, sortOrder });
  }, [performSearch]);

  /**
   * Basculer le panneau de filtres
   */
  const toggleFilterPanel = useCallback(() => {
    dispatch({ type: SEARCH_ACTIONS.TOGGLE_FILTER_PANEL });
  }, []);

  /**
   * Effacer les résultats
   */
  const clearResults = useCallback(() => {
    dispatch({ type: SEARCH_ACTIONS.CLEAR_RESULTS });
  }, []);

  /**
   * Effacer l'erreur
   */
  const clearError = useCallback(() => {
    dispatch({ type: SEARCH_ACTIONS.SET_ERROR, payload: null });
  }, []);

  /**
   * Ajouter aux favoris
   */
  const addToFavorites = useCallback((listingId) => {
    dispatch({ type: SEARCH_ACTIONS.ADD_TO_FAVORITES, payload: listingId });
  }, []);

  /**
   * Retirer des favoris
   */
  const removeFromFavorites = useCallback((listingId) => {
    dispatch({ type: SEARCH_ACTIONS.REMOVE_FROM_FAVORITES, payload: listingId });
  }, []);

  /**
   * Recherche rapide par catégorie
   */
  const searchByCategory = useCallback(async (categoryId) => {
    updateFilters({ categoryId });
    await performSearch({ forceRefresh: true });
  }, [updateFilters, performSearch]);

  /**
   * Recherche par localisation sauvegardée
   */
  const searchBySavedLocation = useCallback(async (location) => {
    updateFilters({ city: location });
    await performSearch({ forceRefresh: true });
  }, [updateFilters, performSearch]);

  // Effets

  /**
   * Recherche automatique quand les filtres changent
   */
  useEffect(() => {
    console.log('🔄 [SearchContext] useEffect triggered - checking if search should be performed:', {
      currentQuery: state.query,
      currentFilters: state.filters,
      hasQuery: !!state.query,
      filterValues: Object.entries(state.filters).map(([key, value]) => ({
        key,
        value,
        isValid: value !== null && value !== '' && value !== false
      })),
      shouldTriggerSearch: state.query || Object.values(state.filters).some(v => v !== null && v !== '' && v !== false),
      timestamp: new Date().toISOString()
    });
    
    if (state.query || Object.values(state.filters).some(v => v !== null && v !== '' && v !== false)) {
      console.log('✅ [SearchContext] Conditions met - scheduling search with 500ms debounce');
      
      const timer = setTimeout(() => {
        console.log('⏰ [SearchContext] Debounce timer fired - calling performSearch');
        performSearch();
      }, 500); // Debounce de 500ms
      
      return () => {
        console.log('🚫 [SearchContext] Debounce timer cancelled');
        clearTimeout(timer);
      };
    } else {
      console.log('❌ [SearchContext] Conditions not met - no search will be performed');
    }
  }, [state.filters]); // Exclure performSearch pour éviter les boucles

  /**
   * Initialiser avec les préférences utilisateur
   */
  useEffect(() => {
    if (user?.preferences?.search) {
      const userPrefs = user.preferences.search;
      
      // Appliquer les préférences par défaut
      dispatch({
        type: SEARCH_ACTIONS.SET_FILTERS,
        payload: {
          radius: userPrefs.defaultRadius || state.filters.radius,
          resultsPerPage: userPrefs.resultsPerPage || state.pagination.limit
        }
      });
      
      // Appliquer la localisation par défaut si disponible
      if (userPrefs.savedLocations?.length > 0) {
        dispatch({
          type: SEARCH_ACTIONS.SET_MAP_CENTER,
          payload: [36.7538, 3.0588] // TODO: Géocoder la première localisation sauvegardée
        });
      }
    }
  }, [user?.preferences]);

  // Valeur du contexte
  const contextValue = {
    // État
    ...state,
    
    selectedListingSource: state.selectedListingSource,
    // Actions
    performSearch,
    forceSearch,
    searchNearby,
    getSuggestions,
    updateQuery,
    updateFilters,
    updateSearchType,
    selectListing,
    changeViewMode,
    updateMapView,
    loadMore,
    sortResults,
    toggleFilterPanel,
    clearResults,
    clearError,
    addToFavorites,
    removeFromFavorites,
    searchByCategory,
    searchBySavedLocation,
    
    // Utilitaires
    isSearchActive: state.query.length > 0 || Object.values(state.filters).some(v => v !== null && v !== '' && v !== false),
    hasResults: state.results.listings.length > 0 || state.productResults.products.length > 0,
    hasListingResults: state.results.listings.length > 0,
    hasProductResults: state.productResults.products.length > 0,
    canLoadMore: state.results.hasMore && !state.isLoading
  };

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
};

// Hook personnalisé
export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export default SearchContext;